import { useEffect, useState } from "react";
import { Row, Col, Card, Statistic, Spin, Alert, Tag, Space, Typography } from "antd";
import {
  WalletOutlined,
  RiseOutlined,
  FallOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { api } from "../../dataProvider";
import { formatMoney, formatDate } from "../../utils/money";

const { Title } = Typography;

interface Summary {
  total_balance: string;
  income_month: string;
  expense_month: string;
  unmatched_count: number;
  gap_date: string | null;
  gap_amount: string | null;
}

interface CashflowDay {
  day: string;
  expected_income: string;
  planned_expense: string;
  running_balance: string;
  alert: string;
}

interface Forecast {
  starting_balance: string;
  end_balance: string;
  min_balance: string;
  has_gap: boolean;
  first_gap_day: string | null;
  days: CashflowDay[];
}

export const DashboardPage: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Summary>("/dashboard/summary"),
      api.get<Forecast>("/forecast/cashflow"),
    ])
      .then(([s, f]) => {
        setSummary(s);
        setForecast(f);
      })
      .catch((e) => setError(e?.message ?? "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message="Ошибка" description={error} />;
  }

  if (!summary) return null;

  // Подготовка данных для графика
  const chartData = (forecast?.days ?? []).map((d) => ({
    day: d.day,
    balance: parseFloat(d.running_balance),
    income: parseFloat(d.expected_income),
    expense: parseFloat(d.planned_expense),
  }));

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Title level={3} style={{ margin: 0 }}>
        Дашборд
      </Title>

      {/* KPI карточки */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Текущий остаток"
              value={parseFloat(summary.total_balance)}
              precision={2}
              prefix={<WalletOutlined />}
              suffix="₽"
              valueStyle={{ color: "#1677ff" }}
              formatter={(v) => formatMoney(v as number).replace(" ₽", "")}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Доходы за месяц"
              value={parseFloat(summary.income_month)}
              precision={2}
              prefix={<RiseOutlined />}
              suffix="₽"
              valueStyle={{ color: "#52c41a" }}
              formatter={(v) => formatMoney(v as number).replace(" ₽", "")}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Расходы за месяц"
              value={parseFloat(summary.expense_month)}
              precision={2}
              prefix={<FallOutlined />}
              suffix="₽"
              valueStyle={{ color: "#fa8c16" }}
              formatter={(v) => formatMoney(v as number).replace(" ₽", "")}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Не размечено"
              value={summary.unmatched_count}
              prefix={<ClockCircleOutlined />}
              valueStyle={{
                color: summary.unmatched_count > 0 ? "#fa8c16" : "#52c41a",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* GAP алерт */}
      {summary.gap_date && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Прогнозируется кассовый разрыв"
          description={
            <Space>
              <span>Первый день в минусе:</span>
              <Tag color="warning">{formatDate(summary.gap_date)}</Tag>
              <span>Баланс:</span>
              <Tag color="error">{formatMoney(summary.gap_amount)}</Tag>
            </Space>
          }
        />
      )}

      {/* Cashflow chart */}
      {chartData.length > 0 && (
        <Card title="Прогноз кассового потока на 90 дней">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="balance" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1677ff" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
              />
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <Tooltip
                formatter={(value: number) => formatMoney(value)}
                labelFormatter={(label) => formatDate(label as string)}
              />
              <ReferenceLine y={0} stroke="#f5222d" strokeDasharray="3 3" />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#1677ff"
                fill="url(#balance)"
                strokeWidth={2}
                name="Остаток"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}
    </Space>
  );
};
